import type { CorpsErreurApi } from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api';

/**
 * Adresse complète d'une route de l'API, pour ce qui ne passe pas par
 * `appelApi` : la source d'une vidéo, un envoi de fichier en flux brut.
 */
export function urlApi(chemin: string): string {
  return `${BASE}${chemin}`;
}

/**
 * Nom de l'évènement diffusé sur `window` quand l'API répond 401 : le cookie
 * de session a expiré ou a été invalidé pendant que l'utilisateur restait sur
 * la page. `AuthContext` l'écoute pour vider `utilisateur`, ce qui déclenche
 * la redirection vers `/connexion` déjà en place dans `ZonePrivee` — sans ça,
 * chaque écran affichait l'erreur brute de sa propre requête au lieu de
 * renvoyer vers la connexion.
 */
export const EVENEMENT_SESSION_EXPIREE = 'tf:session-expiree';

/** Code d'une réponse qui ne vient pas de l'API (voir `appelApi`). */
export const CODE_SERVICE_INDISPONIBLE = 'SERVICE_INDISPONIBLE';

export class ErreurApi extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ErreurApi';
  }

  get estNonAuthentifie(): boolean {
    return this.statut === 401;
  }

  get estAccesRefuse(): boolean {
    return this.statut === 403;
  }

  get estIndisponible(): boolean {
    return this.code === CODE_SERVICE_INDISPONIBLE;
  }
}

interface OptionsAppel {
  methode?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  corps?: unknown;
  /**
   * Envoi de fichier. Exclusif avec `corps` : le navigateur pose lui-même
   * l'en-tête `multipart/form-data` et sa frontière, qu'on ne doit surtout pas
   * écrire à la main.
   */
  formulaire?: FormData;
  signal?: AbortSignal;
  /** La requête survit à la fermeture de la page (position de lecture envoyée en partant). */
  keepalive?: boolean;
}

/**
 * Appel à l'API. Le jeton de session voyage dans un cookie `httpOnly`, donc
 * `credentials: 'include'` est indispensable et il n'y a aucun jeton à stocker
 * côté navigateur.
 */
export async function appelApi<T>(chemin: string, options: OptionsAppel = {}): Promise<T> {
  const reponse = await fetch(`${BASE}${chemin}`, {
    method: options.methode ?? 'GET',
    credentials: 'include',
    headers: options.corps ? { 'Content-Type': 'application/json' } : undefined,
    body: options.formulaire ?? (options.corps ? JSON.stringify(options.corps) : undefined),
    signal: options.signal,
    keepalive: options.keepalive,
  });

  if (reponse.status === 204) return undefined as T;

  const texte = await reponse.text();
  let donnees: unknown = null;
  if (texte) {
    try {
      donnees = JSON.parse(texte);
    } catch {
      // L'API répond toujours en JSON : ce corps vient d'un intermédiaire,
      // Traefik (« 404 page not found ») pendant le redéploiement du conteneur
      // web, nginx (page 502) pendant celui de l'API. Le message brut de
      // `JSON.parse` (« Unexpected non-whitespace character… ») s'affichait.
      throw new ErreurApi(
        reponse.status,
        CODE_SERVICE_INDISPONIBLE,
        'Le service est momentanément indisponible. Réessayez dans un instant.',
      );
    }
  }

  if (!reponse.ok) {
    const corps = donnees as CorpsErreurApi | null;
    if (reponse.status === 401) {
      window.dispatchEvent(new Event(EVENEMENT_SESSION_EXPIREE));
    }
    throw new ErreurApi(
      reponse.status,
      corps?.erreur?.code ?? 'ERREUR',
      corps?.erreur?.message ?? "L'API n'a pas répondu correctement.",
      corps?.erreur?.details,
    );
  }

  return donnees as T;
}
